import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { UnifiedActivitiesService } from './unified-activities.service';
import { UnifiedActivity, ActivityType } from './unified-activity.model';
import { ActivityDayDialogComponent, ActivityDayDialogResult } from './activity-day-dialog.component';
import { ActivityCalendarComponent } from './activity-calendar.component';

interface ActivityDayGroup {
  label: string;
  date: Date;
  items: UnifiedActivity[];
}

@Component({
  selector: 'app-upcoming-activities',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, DatePipe],
  templateUrl: './upcoming-activities.component.html',
  styleUrls: ['./upcoming-activities.component.scss']
})
export class UpcomingActivitiesComponent implements OnInit, OnDestroy {
  private activitiesService = inject(UnifiedActivitiesService);
  private dialog = inject(MatDialog);
  private sub?: Subscription;

  loading = true;
  groups: ActivityDayGroup[] = [];
error = false;
  // Cap rows shown in the widget itself; "View all" goes to the full calendar.
  private readonly maxDaysShown = 3;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  load(): void {
    this.loading = true;
    this.error = false;
    this.sub = this.activitiesService.getUpcomingActivities().subscribe({
      next: (activities) => {
        this.groups = this.groupByDay(activities).slice(0, this.maxDaysShown);
        this.loading = false;
      },
      error: () => { this.loading = false;this.error = true; }
    });
  }

  iconFor(type: ActivityType): string {
    switch (type) {
      case 'telemedicine': return 'videocam';
      case 'event': return 'groups';
      default: return 'calendar_today';
    }
  }

  openCalendar(): void {
    this.dialog.open(ActivityCalendarComponent, {
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh'
    });
  }

  openDay(group: ActivityDayGroup): void {
    const dialogRef = this.dialog.open(ActivityDayDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      data: { date: group.date, activities: group.items }
    });

    dialogRef.afterClosed().subscribe((result: ActivityDayDialogResult | undefined) => {
      if (result?.action === 'view-details') {
        // TODO: wire this to your existing detail dialogs, e.g.:
        // if (result.activity.sourceType === 'Appointment') this.dialog.open(AppointmentDetailsComponent, {...})
        // else this.dialog.open(EventDetailsDialogComponent, {...})
      }
    });
  }

  private groupByDay(activities: UnifiedActivity[]): ActivityDayGroup[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const map = new Map<string, UnifiedActivity[]>();
    for (const activity of activities) {
      if (!activity.start) continue;
      const d = new Date(activity.start);
      const key = d.toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(activity);
    }

    return Array.from(map.entries()).map(([key, items]) => {
      const date = new Date(key);
      let label: string;
      if (date.toDateString() === today.toDateString()) {
        label = 'Today';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        label = 'Tomorrow';
      } else {
        label = date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
      }
      return { label, date, items };
    });
  }
}
